import React, { useMemo } from 'react';
import './Button.css';

import { BaseButton } from './BaseButton';

import { cn } from '@/shared/lib/tailwind-merge';

export enum ButtonSize {
    Small = 'small',
    Medium = 'medium',
    Large = 'large',
    ExtraLarge = 'extraLarge',
}

export enum ButtonVariant {
    Default = 'default',
    Primary = 'primary',
    Secondary = 'secondary',
    Tertiary = 'Tertiary',
    Ghost = 'ghost',
    Link = 'link',
}

type ValueOf<T> = T[keyof T];

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    size?: ValueOf<ButtonSize>;
    variant?: ValueOf<ButtonVariant>;
    name?: string;
    className?: string;
    disabled?: boolean;
    children?: React.ReactNode;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
}

interface ButtonComponent extends React.FC<ButtonProps> {
    types: typeof ButtonVariant;
    sizes: typeof ButtonSize;
}

const Button: ButtonComponent = ({
    size = ButtonSize.Medium,
    variant = ButtonVariant.Primary,
    name,
    className,
    children,
    disabled = false,
    leftIcon,
    rightIcon,
    ...props
}) => {
    const sizeClasses = {
        [ButtonSize.Small]: 'h-[36px] px-[14px] text-md font-semibold',
        [ButtonSize.Medium]: 'h-[40px] px-[16px] text-md font-semibold',
        [ButtonSize.Large]: 'h-[44px] px-[28px] text-md font-semibold',
        [ButtonSize.ExtraLarge]: 'h-[48px] px-[30px] text-md font-semibold',
    };

    const variantClasses = {
        [ButtonVariant.Default]: 'bg-transparent text-content-primary',
        [ButtonVariant.Primary]: `
            bg-background-accent text-content-inversePrimary
            hover:bg-[#4732D4]/90
            focus:outline-none focus:ring-2 focus:ring-border-primary focus:ring-offset-2
            disabled:bg-background-disabled disabled:text-content-secondary
        `,
        [ButtonVariant.Secondary]:
            'border border-color-opaque bg-transparent text-black hover:bg-background-secondary',
        [ButtonVariant.Tertiary]: 'bg-background-secondary text-black hover:bg-[#f5f5f5]',
        [ButtonVariant.Ghost]: 'bg-transparent text-content-primary hover:bg-background-secondary',
        [ButtonVariant.Link]:
            'bg-transparent text-primary underline-offset-4 hover:text-content-accent',
    };

    const testId = useMemo(() => {
        let testId = '';

        if (name) {
            testId = name.toLowerCase().replace(/\s/g, '-');

            return testId + '-button';
        }

        if (!name && typeof children === 'string') {
            testId = children.toLowerCase().replace(/\s/g, '-');

            return testId + '-button';
        }

        return `${size}-${variant}-button`;
    }, [name, children]);

    return (
        <BaseButton
            className={cn(
                'flex justify-center items-center rounded-md transition-shadow duration-200 shadow-none icon-wrapper font-[Inter]',
                sizeClasses[size as keyof typeof sizeClasses],
                variantClasses[variant as keyof typeof variantClasses],
                disabled && 'cursor-not-allowed opacity-70',
                className,
            )}
            disabled={disabled}
            name={name}
            data-testid={testId}
            {...props}
        >
            {leftIcon && (
                <span className='flex items-center h-full icon-wrapper pr-3'>{leftIcon}</span>
            )}
            {children}
            {rightIcon && (
                <span className='flex items-center h-full icon-wrapper pl-3'>{rightIcon}</span>
            )}
        </BaseButton>
    );
};

Button.types = ButtonVariant;
Button.sizes = ButtonSize;

export { Button };
